<script>
import TiptapEditor from '@shared/components/TiptapEditor.vue';
import APITrainings from '@app/services/API/Trainings';

export default {
  components: {
    TiptapEditor,
  },
  props: {
    training: {
      type: Object,
      required: true,
    },
    trainingItem: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      language: 'fr',
      instruction: '',
      rawContent: '',
      contentProcessingMethod: 'generate',
      additionalInstructions: '',
      regenerate: false,
      aiCreditsRequired: 6,
      hasGeneratedContent: false,
    };
  },
  computed: {
    availableAiCredits() {
      return this.$store.getters['auth/aiCreditsLeft'] || 0;
    },
    hasEnoughCredits() {
      return this.availableAiCredits >= this.aiCreditsRequired;
    },
    hasExistingContent() {
      if (!this.trainingItem.content) return false;

      // Handle string content
      if (typeof this.trainingItem.content === 'string') {
        return this.trainingItem.content.trim().length > 0;
      }

      // Handle array content (content blocks)
      if (Array.isArray(this.trainingItem.content)) {
        return this.trainingItem.content.length > 0;
      }

      return false;
    },
  },
  mounted() {
    // Add beforeunload listener to prevent tab closing when content is generated
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },
  beforeDestroy() {
    // Clean up event listener
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  },
  methods: {
    handleBeforeUnload(event) {
      if (this.hasGeneratedContent) {
        const message = 'Du contenu a été généré mais pas encore sauvegardé. Êtes-vous sûr de vouloir quitter cette page ?';
        event.preventDefault();
        event.returnValue = message;
        return message;
      }

      return null;
    },
    confirmClose() {
      if (this.hasGeneratedContent) {
        this.$buefy.dialog.confirm({
          title: 'Attention',
          message: 'Du contenu a été généré mais pas encore sauvegardé. Êtes-vous sûr de vouloir fermer cette fenêtre ? Le contenu sera perdu.',
          confirmText: 'Oui, fermer',
          cancelText: 'Annuler',
          type: 'is-warning',
          onConfirm: () => {
            this.$parent.close();
          },
        });
      } else {
        this.$parent.close();
      }
    },
    async generateContent() {
      if (!this.rawContent.trim() || !this.rawContent.replace(/<[^>]*>/g, '').trim()) {
        this.$buefy.toast.open({
          message: 'Veuillez fournir du contenu source pour la génération',
          type: 'is-warning',
        });
        return;
      }

      if (!this.contentProcessingMethod) {
        this.$buefy.toast.open({
          message: 'Veuillez sélectionner une méthode de traitement du contenu',
          type: 'is-warning',
        });
        return;
      }

      if (!this.hasEnoughCredits) {
        this.$buefy.toast.open({
          message: `Vous n'avez pas assez de crédits IA (${this.aiCreditsRequired} requis)`,
          type: 'is-danger',
        });
        return;
      }

      if (this.hasExistingContent && !this.regenerate) {
        this.$buefy.dialog.confirm({
          message: 'Cette leçon contient déjà du contenu. Voulez-vous le remplacer par le nouveau contenu généré ?',
          type: 'is-warning',
          confirmText: 'Oui, remplacer',
          cancelText: 'Annuler',
          onConfirm: () => this.performGeneration(),
        });
        return;
      }

      this.performGeneration();
    },
    async performGeneration() {
      this.isLoading = true;

      try {
        const payload = {
          language: this.language,
          instruction: this.instruction,
          raw_content: this.rawContent,
          content_processing_method: this.contentProcessingMethod,
          additional_instructions: this.additionalInstructions,
          regenerate: this.regenerate,
        };

        const response = await APITrainings.generateItemAIContent(
          this.training.uuid,
          this.trainingItem.id,
          payload,
        );

        this.hasGeneratedContent = true;

        this.$buefy.toast.open({
          message: 'Contenu généré avec succès !',
          type: 'is-success',
          duration: 4000,
        });

        await this.$store.dispatch('auth/fetch');
        this.$emit('contentGenerated', response.data.content);
        this.$parent.close();
      } catch (error) {
        console.error('Error generating content:', error);
        this.$buefy.toast.open({
          message: (error.response && error.response.data && error.response.data.message) || 'Erreur lors de la génération du contenu',
          type: 'is-danger',
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <div class="modal-card" style="width: 600px;">
    <header class="modal-card-head">
      <p class="modal-card-title">
        <b-icon icon="magic" class="mr-2" />
        Générer le contenu de la leçon
      </p>
      <button type="button" class="modal-close is-large" aria-label="close" @click="confirmClose" />
    </header>

    <section class="modal-card-body">
      <div v-if="hasExistingContent" class="notification is-warning is-light">
        <div class="is-flex is-align-items-center">
          <b-icon icon="exclamation-triangle" class="mr-2" />
          <span>Cette leçon contient déjà du contenu qui sera remplacé par le nouveau contenu généré.</span>
        </div>
      </div>

      <div class="content">
        <div class="mb-4">
          <h4 class="mb-1">
            {{ trainingItem.name }}
          </h4>
          <p class="help mb-0">
            L'IA vous aide à rédiger un contenu pédagogique structuré pour cette leçon,
            en s'appuyant sur le contexte de votre formation.
          </p>
        </div>

        <div class="field">
          <label class="label">Langue</label>
          <div class="control">
            <b-select v-model="language" expanded :loading="isLoading" :disabled="isLoading">
              <option value="fr">Français</option>
              <option value="en">Anglais</option>
            </b-select>
          </div>
        </div>

        <div class="field">
          <label class="label">Comment l'IA doit traiter votre contenu</label>
          <div class="control">
            <b-select v-model="contentProcessingMethod" expanded :loading="isLoading" :disabled="isLoading" required>
              <option value="exact">Utilisez exactement ce contenu dans la leçon</option>
              <option value="paraphrase">Paraphraser et reformuler ce contenu</option>
              <option value="generate">Générer une leçon à partir de ce contenu</option>
            </b-select>
          </div>
        </div>
        <p class="help mb-4">Choisissez comment l'IA utilise le contenu que vous fournissez.</p>

        <div class="field">
          <label class="label">Contenu source</label>
          <div class="control">
            <TiptapEditor :value="rawContent" :height="200" :disabled="isLoading"
              placeholder="Ajoutez ici vos notes, idées, liens ou contenus existants. L'IA s’appuiera sur ces éléments pour générer le contenu de la leçon."
              @input="rawContent = $event" />
          </div>
        </div>
        <p class="help mb-4">
          L'IA s'appuiera sur ce contenu pour générer une leçon structurée.
          Vous pouvez utiliser la mise en forme pour organiser vos idées.
        </p>

        <div class="field">
          <label class="label">Instructions (optionnel)</label>
          <div class="control">
            <b-input v-model="instruction" type="textarea" rows="4" maxlength="2000"
              placeholder="Inclure des exemples pratiques, utiliser un ton professionnel, ajouter des exercices..."
              :loading="isLoading" :disabled="isLoading" expanded />
          </div>
        </div>
        <p class="help mb-4">Donnez des consignes spécifiques à l’IA pour personnaliser le contenu généré
          (par exemple : ton à adopter, niveau de détail, types d’exemples, structure souhaitée).</p>

        <div v-if="!$store.getters['auth/hasEnterpriseCapabilities']" class="notification is-info is-light">
          <div class="is-flex is-justify-content-space-between is-align-items-center">
            <div>
              <strong>Coût :</strong> {{ aiCreditsRequired }} crédits IA
            </div>
            <div>
              <strong>Disponibles :</strong> {{ availableAiCredits }} crédits
            </div>
          </div>
        </div>

        <p class="help has-text-centered mt-10" style="font-size:12px">
          Cette fonctionnalité utilise une IA qui peut, comme toute IA, faire des erreurs.
          Veillez donc à vérifier les contenus générés.
        </p>
      </div>
    </section>

    <footer class="modal-card-foot is-justify-content-flex-end btop-grey">
      <b-button type="is-text" @click="confirmClose">Annuler</b-button>
      <b-button type="is-primary" icon-left="magic" :loading="isLoading"
        :disabled="!hasEnoughCredits || !rawContent.trim() || !rawContent.replace(/<[^>]*>/g, '').trim() || !contentProcessingMethod"
        @click="generateContent">
        Générer le contenu
      </b-button>
    </footer>
  </div>
</template>
